<script setup>
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import { usePlatformPolicy } from 'dashboard/composables/usePlatformPolicy';
import {
  localizedPlanName,
  platformIntlLocale,
  platformTextDirection,
} from 'dashboard/helper/platformLocale';
import { computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const PROVIDER_KEYS = {
  salla: 'SALLA',
  zid: 'ZID',
  shopify: 'SHOPIFY',
};

const route = useRoute();
const { t, locale } = useI18n();
const accountId = computed(() => route.params.accountId);
const { policy, isPolicyLoading } = usePlatformPolicy(accountId);

const renewal = computed(() => policy.value?.renewal || {});
const renewalRequired = computed(() => renewal.value.required === true);
const intlLocale = computed(() => platformIntlLocale(locale.value));
const textDirection = computed(() => platformTextDirection(locale.value));
const providerLabel = computed(() => {
  if (renewal.value.source !== 'marketplace') return 'Salah Makki';
  const provider = String(renewal.value.provider || '').toLowerCase();
  const key = PROVIDER_KEYS[provider] || 'MARKETPLACE';
  return t(`SUBSCRIPTION.PROVIDERS.${key}`);
});
const planDisplayName = computed(() =>
  localizedPlanName({
    locale: locale.value,
    name: renewal.value.plan_name,
    nameAr: renewal.value.plan_name_ar,
    nameEn: renewal.value.plan_name_en,
    code: renewal.value.plan_code,
    fallback: t('SUBSCRIPTION.PLAN.DEFAULT_NAME'),
  })
);
const expiryLabel = computed(() => {
  if (!renewal.value.expires_at) return '';
  const expiryDate = new Date(renewal.value.expires_at);
  if (Number.isNaN(expiryDate.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale.value, {
    dateStyle: 'long',
    timeZone: 'Africa/Cairo',
  }).format(expiryDate);
});
const title = computed(() => {
  if (isPolicyLoading.value) {
    return t('SUBSCRIPTION.SUSPENDED.LOADING_TITLE');
  }
  return renewalRequired.value
    ? t('SUBSCRIPTION.SUSPENDED.EXPIRED_TITLE')
    : t('SUBSCRIPTION.SUSPENDED.ADMIN_TITLE');
});
const message = computed(() => {
  if (isPolicyLoading.value) {
    return t('SUBSCRIPTION.SUSPENDED.LOADING_MESSAGE');
  }
  if (renewalRequired.value && renewal.value.source === 'marketplace') {
    return t('SUBSCRIPTION.SUSPENDED.MARKETPLACE_MESSAGE', {
      provider: providerLabel.value,
    });
  }
  if (renewalRequired.value) {
    return t('SUBSCRIPTION.SUSPENDED.PORTAL_MESSAGE');
  }
  return t('SUBSCRIPTION.SUSPENDED.ADMIN_MESSAGE');
});
const subscriptionRoute = computed(() => ({
  name: 'billing_settings_index',
  params: { accountId: accountId.value },
}));

const toggleSupportWidgetVisibility = () => {
  window.$chatwoot?.toggleBubbleVisibility('show');
};

onMounted(() => {
  toggleSupportWidgetVisibility();
  window.addEventListener('chatwoot:on-message', toggleSupportWidgetVisibility);
});

onUnmounted(() => {
  window.removeEventListener('chatwoot:on-message', toggleSupportWidgetVisibility);
});
</script>

<template>
  <div
    class="items-center bg-n-slate-2 flex justify-center h-full w-full px-4"
    :dir="textDirection"
  >
    <EmptyState :title="title" :message="message">
      <div
        v-if="renewalRequired && !isPolicyLoading"
        class="mx-auto mt-6 max-w-lg border-y border-n-weak py-5 text-start"
      >
        <dl class="grid gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-xs text-n-slate-10">{{ t('SUBSCRIPTION.SUSPENDED.PLAN') }}</dt>
            <dd class="mt-1 text-sm font-medium text-n-slate-12">{{ planDisplayName }}</dd>
          </div>
          <div>
            <dt class="text-xs text-n-slate-10">{{ t('SUBSCRIPTION.SUSPENDED.RENEWAL_PROVIDER') }}</dt>
            <dd class="mt-1 text-sm font-medium text-n-slate-12">{{ providerLabel }}</dd>
          </div>
          <div v-if="expiryLabel" class="sm:col-span-2">
            <dt class="text-xs text-n-slate-10">{{ t('SUBSCRIPTION.SUSPENDED.EXPIRED_AT') }}</dt>
            <dd class="mt-1 text-sm font-medium text-n-slate-12">{{ expiryLabel }}</dd>
          </div>
        </dl>
        <router-link
          :to="subscriptionRoute"
          class="mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-md bg-n-blue-9 px-4 text-sm font-medium text-white hover:bg-n-blue-10"
        >
          <span class="i-lucide-credit-card size-4" />
          {{ t('SUBSCRIPTION.SUSPENDED.REVIEW_ACTION') }}
        </router-link>
      </div>
      <a
        v-else-if="!isPolicyLoading"
        class="mt-5 inline-flex text-sm font-medium text-n-blue-11"
        href="mailto:support@yourtaco.com"
      >
        {{ t('SUBSCRIPTION.SUSPENDED.SUPPORT') }}
      </a>
    </EmptyState>
  </div>
</template>
